import React, { useEffect, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import moment from "moment";
import { Helmet } from "react-helmet";
import hljs from "highlight.js";
// Hooks
import { useHistory, useLocation, useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "../../hooks";

// Actions
import { getSingleArticle } from "../../store/actions";

// Context
import { useGlobalStateContext } from "../../context/globalContext";

// Components
import Title from "../../components/Typography/Title";
import Description from "../../components/Typography/Description";

import Container from "../../components/Container";
import Loader from "../../components/Loader";
import Tags from "../../components/Tags";
import Image from "../../components/Image";
import Social from "../../components/Social";
import Related from "../../components/Related";
import ArticleAuthor from "../../components/ArticleAuthor";

// Pages
import ErrorPage from "../Error";
import ArticleNotFound from "../ArticleNotFound";

// Types
import { RouterState } from "../../components/Grid";
import { Article as ArticleType } from "../../store/actions/types";

// Style
import * as S from "./style";
import { RAZZLE_APP_PORTAL_LINK } from "../../constants";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { cleanArticle } from "../../store/reducers/articles";
import ArticleTagsCaseStudy from "../../components/ArticleTagsCaseStudy";
import Consulenza from "../../components/Consulenza";
import BackToTop from "../../components/BackToTop";

const Article: React.FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const location = useLocation();
  let [articleData, setArticleData] = useState<ArticleType>();
  const { error } = useAppSelector((state) => state.articles);
  const { id } = useParams<RouterState>();
  const { context } = useGlobalStateContext();
  const [IsCaseStudy, setIsCaseStudy] = useState(false);
  const descriptionTitle =
    "Prenota una consulenza gratuita oggi stesso e scopri come possiamo aiutarti a trasformare le tue idee in realtà. Unisciti a noi per creare esperienze digitali che fanno la differenza.";

  const article = useAppSelector(
    (state) => state.articles.data.singleArticle.items[0]
  );

  const { loading: articleInPending } = useSelector(
    (state: RootState) => state.articles.data.singleArticle
  );

  useEffect(() => {
    (async () => {
      await dispatch(cleanArticle());
      await trackPromise(dispatch(getSingleArticle(id)));
    })();
  }, [dispatch, id]);

  useEffect(() => {
    if (article) {
      const {
        parent: { title },
      } = article;

      if (title === "Case Study") {
        context.setIsCaseStudy(true);
        document.body.id = "case-study";
      }
    }
  }, [articleData, article, context, history]);

  useEffect(() => {
    if (article) {
      setArticleData(article);
    }
  }, [article]);

  useEffect(() => {
    if (window !== undefined) {
      document.documentElement.style.overflowX = "hidden";
      document.documentElement.style.overflowY = "initial";
    }
  }, []);

  useEffect(() => {
    if (!article) return;
    hljs.configure({ languages: ["html", "css", "typescript", "gdscript"] });
    document.querySelectorAll(".article__wrapper pre").forEach((block) => {
      hljs.highlightElement(block as HTMLElement);
    });
  }, [article]);

  useEffect(() => {
    const caseStudy = location.pathname.includes("/case-study");
    setIsCaseStudy(caseStudy);
  }, [location.pathname]);

  const renderArticle = ({
    "@id": url,
    id,
    title,
    image,
    image_caption,
    description,
    text,
    subjects,
    created,
    parent,
    creators,
    relatedItems,
    effective,
  }: ArticleType) => {
    const date = moment(effective).format("DD/MM/YYYY");

    return (
      <>
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta
            property="og:image"
            content={image && `${url}/@@download/image/${image.filename}`}
          />
          <meta property="og:image:type" content="image/png" />
          <meta property="twitter:title" content={title} />
          <meta property="twitter:description" content={description} />
          <meta
            property="twitter:image"
            content={image && `${url}/@@download/image/${image.filename}`}
          />
          <meta
            property="og:url"
            content={
              typeof window !== "undefined"
                ? window.location.href
                : `${RAZZLE_APP_PORTAL_LINK}${location.pathname}`
            }
          />
          <link
            rel="canonical"
            href={
              typeof window !== "undefined"
                ? window.location.href
                : `${RAZZLE_APP_PORTAL_LINK}${location.pathname}`
            }
          />
        </Helmet>
        <S.Article IsCaseStudy={IsCaseStudy}>
          <Title title={title} />
          <Description description={description} />
          {IsCaseStudy && (
            <S.ArticleTagsContainer>
              Servizi: <ArticleTagsCaseStudy subjects={subjects} />
            </S.ArticleTagsContainer>
          )}
          <Image
            className="article__img"
            src={image && image.download}
            alt={image_caption}
          />
          <div
            className="article__wrapper"
            dangerouslySetInnerHTML={{ __html: text.data }}
          ></div>
          {IsCaseStudy && (
            <Consulenza
              title="Hai un progetto in mente?"
              description={descriptionTitle}
              book="Prenota"
              mailto="mailto:info@unitiva.it"
            />
          )}
          <S.ArticleInfo flex>
            <Social subjects={subjects} id={id} title={title} parent={parent} />
            <div className="article__info">
              <div className="date">{date}</div>
              <div className="read-time">
                {text && Math.ceil(text.data.split(" ").length / 200)} min di
                lettura
              </div>
            </div>
            {IsCaseStudy ? (
              <ArticleTagsCaseStudy subjects={subjects} />
            ) : (
              <Tags subjects={subjects} />
            )}
          </S.ArticleInfo>
        </S.Article>
        {creators.map((creator) => (
          <ArticleAuthor key={creator} creator={creator} />
        ))}
        <Related items={relatedItems} />
        <BackToTop />
      </>
    );
  };

  if (!article && !articleInPending) {
    // custom message with searched article
    // return <ArticleNotFound title={`No articles found for ${id}`} />;
    return <ArticleNotFound title={`No articles found`} />;
  }

  if (error.message) {
    return <ErrorPage message={error.message} />;
  }

  return (
    <Container>
      {articleInPending ? <Loader /> : article && renderArticle(article)}
    </Container>
  );
};

export default Article;
